import {
  BankAccount,
  Payment,
  PaymentMethod,
  PaymentSchedule,
  Invoice,
  BillingSettings,
  PaymentEvent,
  PaymentEventType,
  PaymentAnalytics,
  PlaidLinkResult,
  PlaidAccountBalance,
  ACHTransferRequest,
  ACHTransferResponse,
  PaymentStatus,
  ACHStatus,
  InvoiceStatus,
  Metadata,
} from '@/types/shared/billing';

export class PlaidPaymentService {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly environment: 'sandbox' | 'development' | 'production';

  constructor() {
    this.baseUrl = import.meta.env.VITE_PLAID_API_URL || 'https://production.plaid.com';
    this.apiKey = import.meta.env.VITE_PLAID_CLIENT_ID || '';
    this.environment =
      (import.meta.env.VITE_PLAID_ENV as 'sandbox' | 'development' | 'production') || 'sandbox';
  }

  // =====================================
  // PLAID LINK & BANK ACCOUNT MANAGEMENT
  // =====================================

  /**
   * Initialize Plaid Link for bank account connection
   */
  async initializePlaidLink(
    customerId: string,
  ): Promise<{ linkToken: string; expiration: string }> {
    try {
      // Mock implementation - in real app, this would call Plaid API
      return {
        linkToken: `link_token_${customerId}_${Date.now()}`,
        expiration: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour
      };
    } catch (error) {
      console.error('Failed to initialize Plaid Link:', error);
      throw new Error('Failed to initialize bank account connection', { cause: error });
    }
  }

  /**
   * Exchange public token for access token and save bank account
   */
  async exchangePublicToken(
    customerId: string,
    publicToken: string,
    metadata: PlaidLinkResult['metadata'],
  ): Promise<BankAccount> {
    try {
      // Mock implementation - in real app, this would call Plaid API
      const accessToken = `access_token_${customerId}_${Date.now()}`;

      const bankAccount: BankAccount = {
        id: `bank_account_${Date.now()}`,
        customerId,
        plaidAccountId: metadata.accounts[0].id,
        plaidAccessToken: accessToken,
        institutionId: metadata.institution.institution_id,
        institutionName: metadata.institution.name,
        accountName: metadata.accounts[0].name,
        accountType: metadata.accounts[0].subtype as 'checking' | 'savings',
        accountMask: metadata.accounts[0].mask,
        isVerified: false,
        isActive: true,
        verificationMethod: 'micro_deposits',
        verificationStatus: 'pending',
        createdAt: new Date().toISOString(),
      };

      // Save to storage
      this.saveBankAccount(bankAccount);

      // Initiate verification process
      await this.initiateAccountVerification(bankAccount.id);

      return bankAccount;
    } catch (error) {
      console.error('Failed to exchange public token:', error);
      throw new Error('Failed to connect bank account', { cause: error });
    }
  }

  /**
   * Verify bank account using micro deposits or instant verification
   */
  async verifyBankAccount(
    bankAccountId: string,
    verificationAmounts?: number[],
  ): Promise<BankAccount> {
    try {
      const bankAccount = this.getBankAccount(bankAccountId);
      if (!bankAccount) {
        throw new Error('Bank account not found');
      }

      // Mock verification - in real app, this would verify with Plaid
      const updatedAccount: BankAccount = {
        ...bankAccount,
        isVerified: true,
        verificationStatus: 'verified',
        verifiedAt: new Date().toISOString(),
      };

      this.saveBankAccount(updatedAccount);

      // Create default payment method
      await this.createPaymentMethod(bankAccount.customerId, {
        type: 'ach',
        bankAccountId: bankAccount.id,
        isDefault: true,
      });

      return updatedAccount;
    } catch (error) {
      console.error('Failed to verify bank account:', error);
      throw new Error('Bank account verification failed', { cause: error });
    }
  }

  /**
   * Get bank account balance from Plaid
   */
  async getBankAccountBalance(bankAccountId: string): Promise<PlaidAccountBalance> {
    try {
      const bankAccount = this.getBankAccount(bankAccountId);
      if (!bankAccount) {
        throw new Error('Bank account not found');
      }

      // Mock balance - in real app, this would fetch from Plaid API
      return {
        accountId: bankAccount.plaidAccountId,
        available: 25000.0,
        current: 25000.0,
        currency: 'USD',
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to get account balance:', error);
      throw new Error('Failed to retrieve account balance', { cause: error });
    }
  }

  // =====================================
  // PAYMENT PROCESSING
  // =====================================

  /**
   * Process ACH transfer
   */
  async processACHTransfer(request: ACHTransferRequest): Promise<Payment> {
    try {
      const bankAccount = this.getBankAccount(request.bankAccountId);
      if (!bankAccount || !bankAccount.isVerified) {
        throw new Error('Bank account not verified');
      }

      const paymentMethod = this.getPaymentMethodByBankAccount(request.bankAccountId);
      if (!paymentMethod) {
        throw new Error('Payment method not found');
      }

      // Calculate processing fees
      const processingFee = this.calculateProcessingFee(request.amount);
      const netAmount = request.amount - processingFee;

      const payment: Payment = {
        id: `payment_${Date.now()}`,
        customerId: request.customerId,
        contractId: '', // Will be set by calling code
        amount: request.amount,
        currency: request.currency,
        status: 'processing',
        paymentMethod,
        processingFee,
        netAmount,
        description: request.description,
        referenceNumber: request.referenceNumber || this.generateReferenceNumber(),
        achStatus: 'initiated',
        initiatedAt: new Date().toISOString(),
        retryCount: 0,
        maxRetries: 3,
        metadata: request.metadata || {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Save payment
      this.savePayment(payment);

      // Record payment event
      await this.recordPaymentEvent(payment.id, 'payment_initiated', 'ACH transfer initiated', {
        amount: request.amount.toString(),
        bankAccount: bankAccount.accountMask,
      });

      // Simulate processing delay
      setTimeout(() => {
        this.updatePaymentStatus(payment.id, 'completed', 'posted');
      }, 5000);

      return payment;
    } catch (error) {
      console.error('Failed to process ACH transfer:', error);
      throw new Error(`Payment processing failed: ${error}`, { cause: error });
    }
  }

  /**
   * Process scheduled payment
   */
  async processScheduledPayment(paymentScheduleId: string): Promise<Payment> {
    try {
      const schedule = this.getPaymentSchedule(paymentScheduleId);
      if (!schedule || !schedule.isActive) {
        throw new Error('Payment schedule not found or inactive');
      }

      const transferRequest: ACHTransferRequest = {
        customerId: schedule.customerId,
        bankAccountId: schedule.paymentMethodId,
        amount: schedule.amount,
        currency: schedule.currency,
        description: `Scheduled payment ${schedule.completedPayments + 1} of ${schedule.totalPayments || 'ongoing'}`,
        referenceNumber: `SCHED_${schedule.id}_${schedule.completedPayments + 1}`,
      };

      const payment = await this.processACHTransfer(transferRequest);

      // Update payment with schedule information
      payment.paymentScheduleId = paymentScheduleId;
      this.savePayment(payment);

      // Update schedule
      schedule.completedPayments++;
      if (schedule.frequency) {
        schedule.nextPaymentDate = this.calculateNextPaymentDate(
          schedule.nextPaymentDate!,
          schedule.frequency,
        );
      }
      this.savePaymentSchedule(schedule);

      return payment;
    } catch (error) {
      console.error('Failed to process scheduled payment:', error);
      throw new Error(`Scheduled payment processing failed: ${error}`, { cause: error });
    }
  }

  /**
   * Refund payment
   */
  async refundPayment(paymentId: string, amount?: number, reason?: string): Promise<Payment> {
    try {
      const originalPayment = this.getPayment(paymentId);
      if (!originalPayment || originalPayment.status !== 'completed') {
        throw new Error('Payment not found or cannot be refunded');
      }

      const refundAmount = amount || originalPayment.amount;
      if (refundAmount > originalPayment.amount) {
        throw new Error('Refund amount cannot exceed original payment amount');
      }

      const refund: Payment = {
        ...originalPayment,
        id: `refund_${Date.now()}`,
        amount: -refundAmount,
        status: 'processing',
        description: `Refund: ${reason || 'Customer request'}`,
        referenceNumber: this.generateReferenceNumber(),
        initiatedAt: new Date().toISOString(),
        metadata: {
          ...originalPayment.metadata,
          originalPaymentId: paymentId,
          refundReason: reason || 'Customer request',
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      this.savePayment(refund);

      // Update original payment status
      originalPayment.status = 'refunded';
      this.savePayment(originalPayment);

      await this.recordPaymentEvent(refund.id, 'payment_initiated', 'Refund initiated', {
        originalPayment: paymentId,
        refundAmount: refundAmount.toString(),
      });

      return refund;
    } catch (error) {
      console.error('Failed to process refund:', error);
      throw new Error(`Refund processing failed: ${error}`, { cause: error });
    }
  }

  // =====================================
  // PAYMENT METHODS & SCHEDULES
  // =====================================

  /**
   * Create payment method
   */
  async createPaymentMethod(
    customerId: string,
    data: {
      type: 'ach' | 'wire' | 'check' | 'credit_card';
      bankAccountId?: string;
      isDefault: boolean;
    },
  ): Promise<PaymentMethod> {
    const paymentMethod: PaymentMethod = {
      id: `pm_${Date.now()}`,
      customerId,
      type: data.type,
      bankAccountId: data.bankAccountId,
      isDefault: data.isDefault,
      isActive: true,
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // If this is set as default, update others
    if (data.isDefault) {
      const existingMethods = this.getPaymentMethodsByCustomer(customerId);
      existingMethods.forEach((method) => {
        method.isDefault = false;
        this.savePaymentMethod(method);
      });
    }

    this.savePaymentMethod(paymentMethod);
    return paymentMethod;
  }

  /**
   * Create payment schedule
   */
  async createPaymentSchedule(
    customerId: string,
    contractId: string,
    data: {
      scheduleType: 'one_time' | 'recurring' | 'milestone_based';
      frequency?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
      amount: number;
      currency: string;
      startDate: string;
      endDate?: string;
      totalPayments?: number;
      paymentMethodId: string;
      autoProcessing: boolean;
    },
  ): Promise<PaymentSchedule> {
    const schedule: PaymentSchedule = {
      id: `schedule_${Date.now()}`,
      customerId,
      contractId,
      scheduleType: data.scheduleType,
      frequency: data.frequency,
      amount: data.amount,
      currency: data.currency,
      startDate: data.startDate,
      endDate: data.endDate,
      totalPayments: data.totalPayments,
      completedPayments: 0,
      paymentMethodId: data.paymentMethodId,
      isActive: true,
      nextPaymentDate: data.startDate,
      autoProcessing: data.autoProcessing,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.savePaymentSchedule(schedule);
    return schedule;
  }

  // =====================================
  // INVOICING
  // =====================================

  /**
   * Create invoice
   */
  async createInvoice(
    customerId: string,
    contractId: string,
    data: {
      lineItems: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
        taxable: boolean;
        category: string;
      }>;
      dueDate: string;
      notes?: string;
    },
  ): Promise<Invoice> {
    const subtotal = data.lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const taxAmount = data.lineItems.reduce((sum, item) => {
      return item.taxable ? sum + item.quantity * item.unitPrice * 0.08 : sum; // 8% tax rate
    }, 0);

    const invoice: Invoice = {
      id: `inv_${Date.now()}`,
      customerId,
      contractId,
      invoiceNumber: this.generateInvoiceNumber(),
      amount: subtotal + taxAmount,
      currency: 'USD',
      status: 'draft',
      lineItems: data.lineItems.map((item, index) => ({
        id: `line_${index + 1}`,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.quantity * item.unitPrice,
        taxable: item.taxable,
        taxRate: item.taxable ? 0.08 : undefined,
        category: item.category,
      })),
      subtotal,
      taxAmount,
      discountAmount: 0,
      totalAmount: subtotal + taxAmount,
      issueDate: new Date().toISOString(),
      dueDate: data.dueDate,
      payments: [],
      amountPaid: 0,
      amountDue: subtotal + taxAmount,
      sentToCustomer: false,
      remindersSent: 0,
      notes: data.notes,
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveInvoice(invoice);
    return invoice;
  }

  /**
   * Send invoice to customer
   */
  async sendInvoice(invoiceId: string): Promise<Invoice> {
    const invoice = this.getInvoice(invoiceId);
    if (!invoice) {
      throw new Error('Invoice not found');
    }

    invoice.status = 'sent';
    invoice.sentToCustomer = true;
    invoice.updatedAt = new Date().toISOString();

    this.saveInvoice(invoice);

    // In real implementation, this would send email notification
    console.log(`Invoice ${invoice.invoiceNumber} sent to customer ${invoice.customerId}`);

    return invoice;
  }

  // =====================================
  // ANALYTICS & REPORTING
  // =====================================

  /**
   * Get payment analytics for customer
   */
  async getPaymentAnalytics(
    customerId: string,
    startDate: string,
    endDate: string,
  ): Promise<PaymentAnalytics> {
    const payments = this.getPaymentsByCustomer(customerId).filter(
      (p) => p.createdAt >= startDate && p.createdAt <= endDate,
    );

    const totalPayments = payments.length;
    const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
    const successfulPayments = payments.filter((p) => p.status === 'completed').length;
    const failedPayments = payments.filter((p) => p.status === 'failed').length;
    const totalProcessingFees = payments.reduce((sum, p) => sum + p.processingFee, 0);

    return {
      customerId,
      period: { startDate, endDate },
      metrics: {
        totalPayments,
        totalAmount,
        successfulPayments,
        failedPayments,
        successRate: totalPayments > 0 ? (successfulPayments / totalPayments) * 100 : 0,
        averagePaymentAmount: totalPayments > 0 ? totalAmount / totalPayments : 0,
        totalProcessingFees,
        netRevenue: totalAmount - totalProcessingFees,
      },
      trends: {
        monthlyVolume: this.calculateMonthlyVolume(payments),
        paymentMethodBreakdown: this.calculatePaymentMethodBreakdown(payments),
      },
    };
  }

  // =====================================
  // UTILITY METHODS
  // =====================================

  private calculateProcessingFee(amount: number): number {
    // 1% + $0.30 processing fee (configurable)
    const percentage = 0.01; // 1%
    const fixed = 0.3; // $0.30
    return amount * percentage + fixed;
  }

  private generateReferenceNumber(): string {
    return `ACH_${Date.now()}_${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  }

  private generateInvoiceNumber(): string {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const sequence = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, '0');
    return `INV-${year}${month}-${sequence}`;
  }

  private calculateNextPaymentDate(currentDate: string, frequency: string): string {
    const date = new Date(currentDate);
    switch (frequency) {
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'semi_annual':
        date.setMonth(date.getMonth() + 6);
        break;
      case 'annual':
        date.setFullYear(date.getFullYear() + 1);
        break;
    }
    return date.toISOString();
  }

  private async initiateAccountVerification(bankAccountId: string): Promise<void> {
    // Mock verification initiation
    console.log(`Initiating verification for bank account ${bankAccountId}`);
  }

  private async updatePaymentStatus(
    paymentId: string,
    status: PaymentStatus,
    achStatus?: ACHStatus,
  ): Promise<void> {
    const payment = this.getPayment(paymentId);
    if (payment) {
      payment.status = status;
      if (achStatus) payment.achStatus = achStatus;
      if (status === 'completed') payment.processedAt = new Date().toISOString();
      if (status === 'failed') payment.failedAt = new Date().toISOString();
      payment.updatedAt = new Date().toISOString();
      this.savePayment(payment);

      await this.recordPaymentEvent(paymentId, 'payment_completed', `Payment ${status}`, {
        newStatus: status,
      });
    }
  }

  private async recordPaymentEvent(
    paymentId: string,
    eventType: PaymentEventType,
    message: string,
    data: Metadata,
  ): Promise<void> {
    const event: PaymentEvent = {
      id: `event_${Date.now()}`,
      paymentId,
      eventType,
      status: 'recorded',
      message,
      data,
      timestamp: new Date().toISOString(),
      source: 'system',
    };

    // Save event (in real app, this would go to database)
    console.log('Payment event recorded:', event);
  }

  private calculateMonthlyVolume(
    payments: Payment[],
  ): Array<{ month: string; amount: number; count: number }> {
    const monthlyData: Record<string, { amount: number; count: number }> = {};

    payments.forEach((payment) => {
      const month = new Date(payment.createdAt).toISOString().substring(0, 7); // YYYY-MM
      if (!monthlyData[month]) {
        monthlyData[month] = { amount: 0, count: 0 };
      }
      monthlyData[month].amount += payment.amount;
      monthlyData[month].count++;
    });

    return Object.entries(monthlyData).map(([month, data]) => ({
      month,
      amount: data.amount,
      count: data.count,
    }));
  }

  private calculatePaymentMethodBreakdown(
    payments: Payment[],
  ): Array<{ method: string; percentage: number; amount: number }> {
    const methodData: Record<string, { count: number; amount: number }> = {};
    const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);

    payments.forEach((payment) => {
      const method = payment.paymentMethod.type;
      if (!methodData[method]) {
        methodData[method] = { count: 0, amount: 0 };
      }
      methodData[method].count++;
      methodData[method].amount += payment.amount;
    });

    return Object.entries(methodData).map(([method, data]) => ({
      method,
      percentage: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0,
      amount: data.amount,
    }));
  }

  // Mock storage methods (in real app, these would interact with database)
  private saveBankAccount(account: BankAccount): void {
    localStorage.setItem(`bank_account_${account.id}`, JSON.stringify(account));
  }

  private getBankAccount(id: string): BankAccount | null {
    const data = localStorage.getItem(`bank_account_${id}`);
    return data ? JSON.parse(data) : null;
  }

  private savePayment(payment: Payment): void {
    localStorage.setItem(`payment_${payment.id}`, JSON.stringify(payment));
  }

  private getPayment(id: string): Payment | null {
    const data = localStorage.getItem(`payment_${id}`);
    return data ? JSON.parse(data) : null;
  }

  private getPaymentsByCustomer(customerId: string): Payment[] {
    // Mock implementation - in real app, this would query database
    return [];
  }

  private savePaymentMethod(method: PaymentMethod): void {
    localStorage.setItem(`payment_method_${method.id}`, JSON.stringify(method));
  }

  private getPaymentMethodsByCustomer(customerId: string): PaymentMethod[] {
    // Mock implementation - in real app, this would query database
    return [];
  }

  private getPaymentMethodByBankAccount(bankAccountId: string): PaymentMethod | null {
    // Mock implementation - in real app, this would query database
    return {
      id: `pm_${bankAccountId}`,
      customerId: 'mock_customer',
      type: 'ach',
      bankAccountId,
      isDefault: true,
      isActive: true,
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  private savePaymentSchedule(schedule: PaymentSchedule): void {
    localStorage.setItem(`payment_schedule_${schedule.id}`, JSON.stringify(schedule));
  }

  private getPaymentSchedule(id: string): PaymentSchedule | null {
    const data = localStorage.getItem(`payment_schedule_${id}`);
    return data ? JSON.parse(data) : null;
  }

  private saveInvoice(invoice: Invoice): void {
    localStorage.setItem(`invoice_${invoice.id}`, JSON.stringify(invoice));
  }

  private getInvoice(id: string): Invoice | null {
    const data = localStorage.getItem(`invoice_${id}`);
    return data ? JSON.parse(data) : null;
  }
}

// Export singleton instance
export const plaidPaymentService = new PlaidPaymentService();
